/**
 * One line per area of the host surface, always in context (#42): in
 * `giraffe_sdk_describe`'s tool description and its no-argument result. An
 * agent that knows nothing about Giraffe reads these to learn what words to
 * search with and which name to open first.
 *
 * `start` names procedures, state keys or recipes to describe first. The
 * control, in `__tests__/hostRpcDomains.test.ts`, holds each one to a name
 * describe resolves.
 */
export type HostRpcDomain = {
  readonly id: string;
  readonly line: string;
  readonly start: readonly [string, ...string[]];
};

export const HOST_RPC_DOMAINS: readonly HostRpcDomain[] = [
  {
    id: "map",
    line: "The camera and the view: fly to a place, fit bounds, read the bounds, plan view, a screenshot, a 3D export.",
    start: ["flyTo", "getMapBounds"],
  },
  {
    id: "layers",
    line:
      "Layers in this project, including parcels and base map layers: the projectLayers and " +
      "layerTree state; show, hide, fade, filter, style, group and reorder; read a layer's " +
      "features; add GeoJSON or an Esri URL as a layer.",
    start: ["projectLayers", "layerTree", "getLayerContents"],
  },
  {
    id: "sections",
    line:
      "Drawn geometry: the rawSections state, the drawing layers Giraffe also calls scenarios, " +
      "and the sections on them.",
    start: ["rawSections", "createDrawingLayer"],
  },
  {
    id: "usages",
    line: "Usages: the land-use types a section can take, and their cost and area assumptions.",
    start: ["getProjectUsages", "updateUsage"],
  },
  {
    id: "analytics",
    line:
      'Analytics (measures, categories, tabs): app "1" in projectAppsByAppID, public.counter. ' +
      "Change it with giraffe_sdk_patch, never updateProjectApp.",
    start: ["projectAppsByAppID", "edit-analytics-measure"],
  },
  {
    id: "project",
    line: "The project and workspace: details, files, other projects, content packs.",
    start: ["project", "fetchProjectDetails"],
  },
];

/**
 * ⚠️ THE CONTROL. A domain that points at a name describe cannot resolve sends
 * an agent's first describe to an error. Empty means sound.
 */
export function domainViolations(
  domains: readonly HostRpcDomain[],
  describableIds: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const domain of domains) {
    if (seen.has(domain.id)) out.push(`domain ${domain.id}: id used twice`);
    seen.add(domain.id);
    if (domain.line.trim() === "") out.push(`domain ${domain.id}: line is empty`);
    for (const id of domain.start) {
      if (!describableIds.has(id))
        out.push(`domain ${domain.id}: start "${id}" is not a name describe resolves`);
    }
  }
  return out;
}
